/**
 * RoadsLegend.tsx — high-traffic roads overlay legend (T-061 amendment).
 *
 * Shown only when the "Major roads" layer is enabled (same visibility idiom as EVLegend).
 * Explains the two independent encodings the overlay ships:
 *   - COLOUR = route type — Interstate #E67E22 (orange), US Route #8E44AD (purple),
 *     other high-traffic major roads in neutral slate #64748b. These hues match
 *     RoadsLayer's line-color match expression exactly.
 *   - LINE WIDTH/OPACITY = traffic volume — heavier-traffic roads (higher AADT tier)
 *     draw thicker and more opaque. Conveyed with one caption line to stay uncluttered.
 *
 * Swatches are short line segments (not dots): roads are line features, so a line reads
 * more honestly than the EV legend's point markers while keeping the same box idiom.
 *
 * T-428 — WHAT THIS LEGEND PROMISES. The slate swatch used to read "Arterial", naming a road
 * CLASS the overlay does not carry. The ingest keeps Interstates and US Routes unconditionally
 * plus any OTHER road at >= 10k AADT (db/seeds/load_roads.sh), so most of the arterial class is
 * absent by design. Labelled as a class, a missing local arterial made the map look broken; the
 * data was never the gap, the promise was. The basemap already draws every street underneath —
 * this overlay is a traffic-volume overlay, not the road network, and now says so.
 *
 * The threshold rides ON the slate label instead of a trailing caption for two reasons:
 *   - ACCURACY: the floor applies only to this bucket. Interstates and US Routes come in
 *     regardless of AADT, so a legend-wide "10k+" caption would misstate both of them.
 *   - LAYOUT: this stack is bottom-anchored and grows UPWARD. A 4th line here cost 29px and
 *     pushed the Opportunity Score box off the top of the viewport at 560px height (measured
 *     +27px -> -2px). Lines in this box are not free; keep the count at three plus the caption.
 *
 * The "10k+" tracks AADT_FLOOR's default in db/seeds/load_roads.sh, stated as a number
 * deliberately — "high-traffic" alone does not tell a user why their road is missing, which is
 * the entire point. If that floor is ever reloaded at a different value, this label moves with it.
 */
const ROUTE_TYPES = [
  { label: 'Interstate', color: '#E67E22' },
  { label: 'US Route', color: '#8E44AD' },
  { label: 'Other road, 10k+ daily traffic', color: '#64748b' },
]

export function RoadsLegend() {
  return (
    <div className="glass rounded-lg border border-border/60 shadow-md px-2.5 py-2 text-[10px] w-48">
      <p className="font-semibold text-muted-foreground mb-1">🛣️ Major roads</p>
      <div className="space-y-0.5">
        {ROUTE_TYPES.map((r) => (
          <div key={r.label} className="flex items-center gap-1.5">
            <span
              className="w-4 h-[3px] rounded-full shrink-0"
              style={{ backgroundColor: r.color }}
              aria-hidden="true"
            />
            <span className="text-muted-foreground">{r.label}</span>
          </div>
        ))}
      </div>
      <p className="text-muted-foreground/80 mt-1 leading-tight">Thicker line = heavier traffic</p>
    </div>
  )
}
